import { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import PatientNav from './PatientNav';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { FaPlus, FaSearch, FaFileInvoiceDollar, FaCalendar, FaStethoscope, FaTimes, FaPrint, FaDownload } from 'react-icons/fa';
import Loader from '../components/Loader';

const Invoice = () => {
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { nationalId } = useParams();

  const [newInvoice, setNewInvoice] = useState({
    date: '',
    dueDate: '',
    patient: nationalId,
    doctor: user?.id || '',
    items: [{ description: '', quantity: 1, unitPrice: '', total: '' }],
    subtotal: 0,
    tax: 0,
    total: 0,
    status: 'pending',
    notes: '',
    paymentMethod: 'cash'
  });

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        console.log('Fetching invoices for nationalId:', nationalId);
        // This endpoint needs to be created in the backend
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/invoices/patient/${nationalId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('Invoices fetched:', response.data);
        setInvoices(response.data || []);
      } catch (err) {
        console.error('Error fetching invoices:', err.response?.data || err.message);
        // For now, set empty array if endpoint doesn't exist
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };
    if (token && nationalId) fetchInvoices();
  }, [nationalId, token]);

  const validateForm = () => {
    if (!newInvoice.date) return 'Date is required';
    if (!newInvoice.dueDate) return 'Due date is required';
    if (newInvoice.items.length === 0) return 'At least one item is required';
    if (newInvoice.items.some(item => !item.description || !item.unitPrice)) {
      return 'All items must have description and unit price';
    }
    return '';
  };

  const handleAddInvoice = async (e) => {
    e.preventDefault();
    if (!user?.id) {
      setError('You must be logged in as a dentist to create invoices.');
      return;
    }
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      console.log('Creating invoice:', newInvoice);
      // This endpoint needs to be created in the backend
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/invoices`, newInvoice, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Invoice created:', response.data);
      setInvoices([response.data, ...invoices]);
      setNewInvoice({
        date: '',
        dueDate: '',
        patient: nationalId,
        doctor: user.id,
        items: [{ description: '', quantity: 1, unitPrice: '', total: '' }],
        subtotal: 0,
        tax: 0,
        total: 0,
        status: 'pending',
        notes: '',
        paymentMethod: 'cash'
      });
      setShowAddForm(false);
      setError('');
    } catch (err) {
      console.error('Error creating invoice:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Failed to create invoice');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewInvoice({ ...newInvoice, [name]: value });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...newInvoice.items];
    updatedItems[index][field] = value;
    
    // Calculate total for this item
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = parseFloat(updatedItems[index].quantity) || 0;
      const unitPrice = parseFloat(updatedItems[index].unitPrice) || 0;
      updatedItems[index].total = (quantity * unitPrice).toFixed(2);
    }
    
    setNewInvoice({ ...newInvoice, items: updatedItems });
    calculateTotals(updatedItems);
  };

  const addItem = () => {
    setNewInvoice({
      ...newInvoice,
      items: [...newInvoice.items, { description: '', quantity: 1, unitPrice: '', total: '' }]
    });
  };

  const removeItem = (index) => {
    const updatedItems = newInvoice.items.filter((_, i) => i !== index);
    setNewInvoice({ ...newInvoice, items: updatedItems });
    calculateTotals(updatedItems);
  };

  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    const tax = subtotal * 0.15; // 15% tax
    const total = subtotal + tax;
    
    setNewInvoice(prev => ({
      ...prev,
      subtotal: subtotal.toFixed(2),
      tax: tax.toFixed(2),
      total: total.toFixed(2)
    }));
  };

  const filteredInvoices = invoices.filter((invoice) =>
    invoice.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateValue) => {
    try {
      let date;
      if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue && dateValue.toDate) {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (dateValue && dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else {
        return 'Date not available';
      }
      
      if (isNaN(date.getTime())) {
        return 'Date not available';
      }
      
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date not available';
    }
  };

  const deleteInvoice = async (id) => {
    try {
      console.log('Deleting invoice:', id);
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(invoices.filter((invoice) => invoice._id !== id));
      setShowInvoiceDetails(null);
    } catch (err) {
      console.error('Error deleting invoice:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Failed to delete invoice');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'overdue': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  if (!user || user.role !== 'dentist') {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[#0077B6]/5 to-white">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center max-w-md bg-white p-8 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="text-[#0077B6] mb-4">
                  <FaStethoscope className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Access Denied</h3>
                <p className="text-gray-600 mb-6">Please log in as a dentist to view this page.</p>
              </motion.div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <PatientNav />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[#0077B6]/5 to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mb-6 p-4 bg-red-100 text-[#333333] rounded-lg"
              >
                {error}
              </motion.div>
            )}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#0077B6] mb-1">
                    Invoices
                  </h1>
                  <p className="text-gray-600">Create and manage patient invoices</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAddForm(true)}
                  className="w-full md:w-auto bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white px-6 py-2.5 rounded-full font-medium hover:from-[#0077B6]/90 hover:to-[#20B2AA]/90 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                >
                  <FaPlus className="h-5 w-5 mr-2" />
                  New Invoice
                </motion.button>
              </div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="relative mb-6"
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search invoices..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-full bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0077B6] focus:bg-white sm:text-sm transition-all duration-200"
                />
              </motion.div>
              <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                {filteredInvoices.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-8 text-center"
                  >
                    <div className="mx-auto h-16 w-16 text-gray-400 mb-4">
                      <FaFileInvoiceDollar className="h-16 w-16" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">No invoices found</h3>
                    <p className="text-gray-500">
                      {searchTerm ? 'Try a different search term' : 'Get started by creating a new invoice'}
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowAddForm(true)}
                      className="mt-6 inline-flex items-center px-6 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full hover:from-[#0077B6]/90 hover:to-[#20B2AA]/90 font-medium shadow-md hover:shadow-lg"
                    >
                      <FaPlus className="h-5 w-5 mr-2" />
                      Create Invoice
                    </motion.button>
                  </motion.div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {filteredInvoices.map((invoice) => (
                      <motion.li
                        key={invoice._id}
                        variants={item}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => setShowInvoiceDetails(invoice)}
                      >
                        <div className="px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-3 w-3 rounded-full bg-[#FFD700]"></div>
                              <h3 className="text-lg font-medium text-gray-800">Invoice #{invoice._id?.slice(-6) || 'N/A'}</h3>
                            </div>
                            <div className="text-sm text-gray-500">{formatDate(invoice.date)}</div>
                          </div>
                          <div className="mt-2 flex justify-between items-center">
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Total:</span> ${invoice.total || '0.00'}
                            </p>
                            <div className="flex gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                                {invoice.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.li>
                    ))}
                  </ul>
                )}
              </motion.div>
            </motion.div>
            {showAddForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
                >
                  <div className="p-6 border-b flex justify-between items-center bg-[#0077B6]/10">
                    <h2 className="text-xl font-semibold text-[#0077B6]">New Invoice</h2>
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FaTimes className="h-6 w-6" />
                    </button>
                  </div>
                  <form onSubmit={handleAddInvoice} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Date*</label>
                        <div className="relative">
                          <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="date"
                            name="date"
                            value={newInvoice.date}
                            onChange={handleInputChange}
                            className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Due Date*</label>
                        <div className="relative">
                          <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="date"
                            name="dueDate"
                            value={newInvoice.dueDate}
                            onChange={handleInputChange}
                            className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Payment Method</label>
                        <select
                          name="paymentMethod"
                          value={newInvoice.paymentMethod}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                        >
                          <option value="cash">Cash</option>
                          <option value="card">Card</option>
                          <option value="insurance">Insurance</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">Invoice Items</h3>
                        <button
                          type="button"
                          onClick={addItem}
                          className="px-3 py-1 bg-[#0077B6] text-white rounded-lg hover:bg-[#005A8B] transition-colors text-sm"
                        >
                          + Add Item
                        </button>
                      </div>
                      <div className="space-y-4">
                        {newInvoice.items.map((item, index) => (
                          <div key={index} className="grid grid-cols-12 gap-4 items-center">
                            <div className="col-span-5">
                              <input
                                type="text"
                                placeholder="Description"
                                value={item.description}
                                onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                                required
                              />
                            </div>
                            <div className="col-span-2">
                              <input
                                type="number"
                                placeholder="Qty"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                                min="1"
                                required
                              />
                            </div>
                            <div className="col-span-2">
                              <input
                                type="number"
                                placeholder="Unit Price"
                                value={item.unitPrice}
                                onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                                step="0.01"
                                min="0"
                                required
                              />
                            </div>
                            <div className="col-span-2">
                              <input
                                type="text"
                                placeholder="Total"
                                value={item.total}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                readOnly
                              />
                            </div>
                            <div className="col-span-1">
                              {newInvoice.items.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeItem(index)}
                                  className="w-full px-2 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <FaTimes className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">${newInvoice.subtotal}</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">Tax (15%):</span>
                        <span className="font-medium">${newInvoice.tax}</span>
                      </div>
                      <div className="flex justify-between items-center text-lg font-bold">
                        <span className="text-gray-800">Total:</span>
                        <span className="text-[#0077B6]">${newInvoice.total}</span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes (Optional)</label>
                      <textarea
                        name="notes"
                        value={newInvoice.notes}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                        rows="3"
                      />
                    </div>

                    <div className="flex justify-end space-x-4">
                      <motion.button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        type="submit"
                        className="px-6 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full hover:from-[#0077B6]/90 hover:to-[#20B2AA]/90 font-medium transition-colors shadow-md hover:shadow-lg"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Create Invoice
                      </motion.button>
                    </div>
                  </form>
                </motion.div>
              </div>
            )}
            {showInvoiceDetails && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
                >
                  <div className="p-6 border-b flex justify-between items-center bg-[#0077B6]/10">
                    <h2 className="text-xl font-semibold text-[#0077B6]">Invoice Details</h2>
                    <div className="flex gap-2">
                      <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                        <FaPrint className="h-5 w-5" />
                      </button>
                      <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                        <FaDownload className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setShowInvoiceDetails(null)}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <FaTimes className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Invoice Date</p>
                        <p className="text-sm text-gray-900">{formatDate(showInvoiceDetails.date)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Due Date</p>
                        <p className="text-sm text-gray-900">{formatDate(showInvoiceDetails.dueDate)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Status</p>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(showInvoiceDetails.status)}`}>
                          {showInvoiceDetails.status}
                        </span>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Items</h3>
                      <div className="space-y-3">
                        {showInvoiceDetails.items?.map((item, index) => (
                          <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{item.description}</p>
                              <p className="text-sm text-gray-500">Qty: {item.quantity} × ${item.unitPrice}</p>
                            </div>
                            <p className="font-medium text-gray-900">${item.total}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">${showInvoiceDetails.subtotal}</span>
                      </div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">Tax (15%):</span>
                        <span className="font-medium">${showInvoiceDetails.tax}</span>
                      </div>
                      <div className="flex justify-between items-center text-lg font-bold border-t pt-2">
                        <span className="text-gray-800">Total:</span>
                        <span className="text-[#0077B6]">${showInvoiceDetails.total}</span>
                      </div>
                    </div>

                    {showInvoiceDetails.notes && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Notes</p>
                        <p className="text-sm text-gray-900">{showInvoiceDetails.notes}</p>
                      </div>
                    )}

                    <div className="flex justify-end gap-3">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => deleteInvoice(showInvoiceDetails._id)}
                        className="px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full hover:from-red-600 hover:to-red-700 font-medium transition-colors shadow-md"
                      >
                        Delete
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Invoice;

